import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import T from 'i18n';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import Menu from '@mui/material/Menu';
import type { RealTimeTheme } from '../theme';
import type { Visibility, VisibilitySettings } from '../types';

type ThemeSettingsMenuProps = {
  theme: RealTimeTheme;
  disabled?: boolean;
  onThemeChange: (theme: RealTimeTheme) => void;
  showTimestamps: boolean;
  onTimestampsChange: (show: boolean) => void;
  visibilitySettings: VisibilitySettings;
  onVisibilityChange: (kind: keyof VisibilitySettings, value: Visibility) => void;
};

const ThemeSettingsMenu: React.FC<ThemeSettingsMenuProps> = ({
  theme,
  disabled = false,
  onThemeChange,
  showTimestamps,
  onTimestampsChange,
  visibilitySettings,
  onVisibilityChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const menuId = 'real-time-settings-menu';

  const handleOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelectTheme = useCallback(
    (nextTheme: RealTimeTheme) => {
      if (disabled || nextTheme === theme) return;
      onThemeChange(nextTheme);
      setAnchorEl(null);
    },
    [disabled, onThemeChange, theme],
  );

  const handleSelectTimestamps = useCallback(
    (show: boolean) => {
      if (disabled || show === showTimestamps) return;
      onTimestampsChange(show);
      setAnchorEl(null);
    },
    [disabled, onTimestampsChange, showTimestamps],
  );

  const handleSelectVisibility = useCallback(
    (kind: keyof VisibilitySettings, value: Visibility) => {
      if (disabled || visibilitySettings[kind] === value) return;
      onVisibilityChange(kind, value);
    },
    [disabled, visibilitySettings, onVisibilityChange],
  );

  return (
    <div className="real-time__settings">
      <button
        type="button"
        className="real-time__settings-btn"
        onClick={handleOpen}
        aria-label={T.translate('case.realTimeSettings', { defaultValue: 'Transcript settings' })}
        aria-controls={open ? menuId : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="menu"
      >
        <SettingsRoundedIcon fontSize="small" />
      </button>
      <Menu
        id={menuId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        disablePortal
        transitionDuration={0}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{
          'aria-label': T.translate('case.realTimeSettings', {
            defaultValue: 'Transcript settings',
          }) as string,
        }}
        PaperProps={{ className: 'real-time__settings-menu' }}
      >
        <div className="real-time__settings-menu-section">
          <div className="real-time__settings-menu-title">
            {T.translate('case.realTimeTheme', { defaultValue: 'Theme' })}
          </div>
          <div
            className="real-time__theme-toggle"
            role="group"
            aria-label={T.translate('case.realTimeTheme', { defaultValue: 'Theme' }) as string}
          >
            <button
              type="button"
              className={classnames('real-time__theme-toggle-btn', {
                'is-active': theme === 'dark',
              })}
              onClick={() => handleSelectTheme('dark')}
              aria-pressed={theme === 'dark'}
              disabled={disabled}
            >
              {T.translate('case.dark', { defaultValue: 'Dark' })}
            </button>
            <button
              type="button"
              className={classnames('real-time__theme-toggle-btn', {
                'is-active': theme === 'light',
              })}
              onClick={() => handleSelectTheme('light')}
              aria-pressed={theme === 'light'}
              disabled={disabled}
            >
              {T.translate('case.light', { defaultValue: 'Light' })}
            </button>
          </div>
        </div>
        <div className="real-time__settings-menu-divider" />
        <div className="real-time__settings-menu-section">
          <div className="real-time__settings-menu-title">
            {T.translate('case.realTimeTimestamps', { defaultValue: 'Timestamps' })}
          </div>
          <div
            className="real-time__theme-toggle"
            role="group"
            aria-label={
              T.translate('case.realTimeTimestamps', { defaultValue: 'Timestamps' }) as string
            }
          >
            <button
              type="button"
              className={classnames('real-time__theme-toggle-btn', {
                'is-active': showTimestamps,
              })}
              onClick={() => handleSelectTimestamps(true)}
              aria-pressed={showTimestamps}
              disabled={disabled}
            >
              {T.translate('case.show', { defaultValue: 'Show' })}
            </button>
            <button
              type="button"
              className={classnames('real-time__theme-toggle-btn', {
                'is-active': !showTimestamps,
              })}
              onClick={() => handleSelectTimestamps(false)}
              aria-pressed={!showTimestamps}
              disabled={disabled}
            >
              {T.translate('case.hide', { defaultValue: 'Hide' })}
            </button>
          </div>
        </div>
        <div className="real-time__settings-menu-divider" />
        <div className="real-time__settings-menu-section">
          <div className="real-time__settings-menu-title">
            {T.translate('case.realTimeShareHighlights', {
              defaultValue: 'Share All Highlights',
            })}
          </div>
          <div
            className="real-time__theme-toggle"
            role="group"
            aria-label={
              T.translate('case.realTimeShareHighlights', {
                defaultValue: 'Share All Highlights',
              }) as string
            }
          >
            <button
              type="button"
              className={classnames('real-time__theme-toggle-btn', {
                'is-active': visibilitySettings.highlight === 'team',
              })}
              onClick={() => handleSelectVisibility('highlight', 'team')}
              aria-pressed={visibilitySettings.highlight === 'team'}
              disabled={disabled}
            >
              {T.translate('case.yes', { defaultValue: 'Yes' })}
            </button>
            <button
              type="button"
              className={classnames('real-time__theme-toggle-btn', {
                'is-active': visibilitySettings.highlight === 'private',
              })}
              onClick={() => handleSelectVisibility('highlight', 'private')}
              aria-pressed={visibilitySettings.highlight === 'private'}
              disabled={disabled}
            >
              {T.translate('case.no', { defaultValue: 'No' })}
            </button>
          </div>
        </div>
        <div className="real-time__settings-menu-divider" />
        <div className="real-time__settings-menu-section">
          <div className="real-time__settings-menu-title">
            {T.translate('case.realTimeShareNotes', {
              defaultValue: 'Share All Notes',
            })}
          </div>
          <div
            className="real-time__theme-toggle"
            role="group"
            aria-label={
              T.translate('case.realTimeShareNotes', {
                defaultValue: 'Share All Notes',
              }) as string
            }
          >
            <button
              type="button"
              className={classnames('real-time__theme-toggle-btn', {
                'is-active': visibilitySettings.note === 'team',
              })}
              onClick={() => handleSelectVisibility('note', 'team')}
              aria-pressed={visibilitySettings.note === 'team'}
              disabled={disabled}
            >
              {T.translate('case.yes', { defaultValue: 'Yes' })}
            </button>
            <button
              type="button"
              className={classnames('real-time__theme-toggle-btn', {
                'is-active': visibilitySettings.note === 'private',
              })}
              onClick={() => handleSelectVisibility('note', 'private')}
              aria-pressed={visibilitySettings.note === 'private'}
              disabled={disabled}
            >
              {T.translate('case.no', { defaultValue: 'No' })}
            </button>
          </div>
        </div>
        <div className="real-time__settings-menu-divider" />
        <div className="real-time__settings-menu-section">
          <div className="real-time__settings-menu-title">
            {T.translate('case.realTimeShareTags', {
              defaultValue: 'Share All Tags',
            })}
          </div>
          <div
            className="real-time__theme-toggle"
            role="group"
            aria-label={
              T.translate('case.realTimeShareTags', {
                defaultValue: 'Share All Tags',
              }) as string
            }
          >
            <button
              type="button"
              className={classnames('real-time__theme-toggle-btn', {
                'is-active': visibilitySettings.tag === 'team',
              })}
              onClick={() => handleSelectVisibility('tag', 'team')}
              aria-pressed={visibilitySettings.tag === 'team'}
              disabled={disabled}
            >
              {T.translate('case.yes', { defaultValue: 'Yes' })}
            </button>
            <button
              type="button"
              className={classnames('real-time__theme-toggle-btn', {
                'is-active': visibilitySettings.tag === 'private',
              })}
              onClick={() => handleSelectVisibility('tag', 'private')}
              aria-pressed={visibilitySettings.tag === 'private'}
              disabled={disabled}
            >
              {T.translate('case.no', { defaultValue: 'No' })}
            </button>
          </div>
        </div>
      </Menu>
    </div>
  );
};

export default ThemeSettingsMenu;
